import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import { parseSpreadsheetMlRows } from './OrganizationsBulkImportModal';

const TEMPLATE_HEADERS = [
  'full_name',
  'role',
  'email',
  'phone',
  'supplier_name',
  'status',
  'created_by',
];

const COLUMN_LABELS = {
  full_name: 'Nombre',
  role: 'Cargo',
  email: 'Correo',
  phone: 'Teléfono',
  supplier_name: 'Proveedor',
  status: 'Estado',
  created_by: 'Creado por',
};

const TEMPLATE_EXAMPLE_CELLS = [
  'Juan Pérez',
  'Compras',
  'juan.perez@ejemplo.cl',
  '+56912345678',
  'Proveedor Ejemplo SpA',
  'Activo',
  'sistema',
];

const emptyRow = () =>
  TEMPLATE_HEADERS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

export const downloadSupplierContactsExcelTemplate = () => {
  const headerCells = TEMPLATE_HEADERS.map(
    (header) => `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>`
  ).join('');
  const exampleCells = TEMPLATE_EXAMPLE_CELLS.map(
    (cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
  ).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Contactos">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_contactos_proveedores.xls';
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const HEADER_ALIASES = {
  full_name: ['full_name', 'nombre', 'contacto', 'name'],
  role: ['role', 'cargo', 'puesto'],
  email: ['email', 'correo', 'mail'],
  phone: ['phone', 'telefono', 'fono', 'tel'],
  supplier_name: ['supplier_name', 'proveedor', 'nombre_proveedor', 'supplier'],
  status: ['status', 'estado'],
  created_by: ['created_by', 'creado_por', 'responsable'],
};

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis >= commas) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const mapHeaderToField = (header) => {
  const key = normalizeHeader(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(key)) return field;
  }
  return TEMPLATE_HEADERS.includes(key) ? key : null;
};

const looksLikeSpreadsheetMl = (text = '') => {
  const sample = String(text || '').slice(0, 4000).toLowerCase();
  return (
    sample.includes('mso-application') ||
    sample.includes('urn:schemas-microsoft-com:office:spreadsheet') ||
    (sample.includes('<workbook') && sample.includes('<worksheet'))
  );
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '');
  if (!sample) return false;
  if (sample.charCodeAt(0) === 0x50 && sample.charCodeAt(1) === 0x4b) return true;
  const nulls = [...sample.slice(0, 2000)].filter((ch) => ch.charCodeAt(0) === 0).length;
  return nulls > 20;
};

const buildRowsFromCellTable = (table = []) => {
  if (!table.length) return { rows: [], error: 'No se encontraron filas.' };
  const headerRow = table[0] || [];
  const mapping = headerRow.map(mapHeaderToField);
  if (!mapping.includes('full_name')) {
    return { rows: [], error: 'Falta la columna full_name / nombre en el encabezado.' };
  }
  const rows = [];
  for (let i = 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    if (!cells.some((cell) => String(cell || '').trim())) continue;
    const row = emptyRow();
    mapping.forEach((field, idx) => {
      if (!field) return;
      row[field] = String(cells[idx] || '').trim();
    });
    if (!row.full_name) continue;
    rows.push(row);
  }
  if (!rows.length) {
    return { rows: [], error: 'No se encontraron filas con nombre de contacto.' };
  }
  return { rows, error: '' };
};

export const parseSupplierContactsCsv = (rawText = '') => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV o la plantilla Excel (.xls).' };
  if (looksLikeBinaryOffice(text)) {
    return {
      rows: [],
      error:
        'Este archivo parece .xlsx/.xls binario. Usa la plantilla Excel descargada aquí, o exporta a CSV (separado por ;).',
    };
  }
  if (looksLikeSpreadsheetMl(text)) {
    return buildRowsFromCellTable(parseSpreadsheetMlRows(text));
  }
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { rows: [], error: 'El archivo está vacío.' };
  const delimiter = detectDelimiter(lines[0]);
  const table = lines.map((line) => splitCsvLine(line, delimiter));
  return buildRowsFromCellTable(table);
};

const foldKey = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const homologateSupplierContactImportRow = (row = {}) => {
  const next = { ...emptyRow(), ...row };
  next.full_name = String(next.full_name || '').trim();
  next.role = String(next.role || '').trim();
  next.email = String(next.email || '')
    .trim()
    .toLowerCase();
  next.phone = String(next.phone || '').trim();
  next.supplier_name = String(next.supplier_name || '').trim();
  const statusFold = foldKey(next.status);
  next.status = statusFold === 'inactivo' ? 'Inactivo' : 'Activo';
  next.created_by = String(next.created_by || '').trim();
  return next;
};

export const getSupplierContactImportFieldErrors = (row = {}) => {
  const errors = {};
  if (!String(row.full_name || '').trim()) errors.full_name = 'Obligatorio';
  if (!String(row.supplier_name || '').trim()) {
    errors.supplier_name = 'Obligatorio';
  }
  const email = String(row.email || '').trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Email inválido';
  }
  return errors;
};

const toApiPayload = (row) => ({
  full_name: String(row.full_name || '').trim(),
  role: String(row.role || '').trim(),
  email: String(row.email || '').trim().toLowerCase(),
  phone: String(row.phone || '').trim(),
  supplier_name: String(row.supplier_name || '').trim(),
  status: String(row.status || 'Activo').trim() || 'Activo',
  created_by: String(row.created_by || '').trim() || 'system',
});

/**
 * Carga masiva de contactos de proveedores.
 */
const SupplierContactsBulkImportModal = ({
  isOpen = false,
  onClose,
  onImport,
  isImporting = false,
  isDarkMode = false,
  result = null,
}) => {
  const [step, setStep] = useState('source');
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep('source');
    setRawText('');
    setPreviewRows([]);
    setLocalError('');
    setLocalSuccess('');
  }, [isOpen]);

  const colors = {
    text: isDarkMode ? '#f4f4f5' : '#0f172a',
    muted: isDarkMode ? '#a1a1aa' : '#64748b',
    border: isDarkMode ? '#3f3f46' : '#e2e8f0',
    inputBg: isDarkMode ? '#18181b' : '#fff',
    inputBorder: isDarkMode ? '#3f3f46' : '#cbd5e1',
    panel: isDarkMode ? '#09090b' : '#fff',
  };

  const parsed = useMemo(() => parseSupplierContactsCsv(rawText), [rawText]);

  const rowErrors = useMemo(
    () => previewRows.map((row) => getSupplierContactImportFieldErrors(row)),
    [previewRows]
  );
  const invalidFieldCount = rowErrors.reduce((sum, err) => sum + Object.keys(err).length, 0);
  const errorSummaries = useMemo(() => {
    const items = [];
    rowErrors.forEach((err, index) => {
      Object.entries(err || {}).forEach(([field, message]) => {
        items.push({
          row: index + 1,
          field,
          label: COLUMN_LABELS[field] || field,
          message,
        });
      });
    });
    return items;
  }, [rowErrors]);

  if (!isOpen) return null;

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setRawText(text);
      setLocalError('');
      setLocalSuccess('');
    } catch {
      setLocalError('No se pudo leer el archivo.');
    }
  };

  const handleContinueFromSource = () => {
    if (parsed.error || !parsed.rows.length) {
      setLocalError(parsed.error || 'No hay filas para revisar.');
      return;
    }
    setPreviewRows(parsed.rows.map((row) => homologateSupplierContactImportRow(row)));
    setStep('preview');
    setLocalError('');
    setLocalSuccess('');
  };

  const updatePreviewCell = (index, key, value) => {
    setPreviewRows((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [key]: value };
        if (key === 'email') next.email = String(value || '').trim().toLowerCase();
        if (key === 'status') {
          next.status = foldKey(value) === 'inactivo' ? 'Inactivo' : 'Activo';
        }
        return next;
      })
    );
  };

  const removePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!previewRows.length || invalidFieldCount > 0 || isImporting || localSuccess) return;
    setLocalError('');
    try {
      const data = await onImport?.(previewRows.map(toApiPayload));
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors === 0 || created > 0) {
        setLocalSuccess(
          `Importación exitosa: ${created} contacto(s) creados` +
            (skipped ? `, ${skipped} omitidos` : '') +
            '.'
        );
      }
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={() => !isImporting && onClose?.()}
    >
      <div
        style={{
          width: 'min(1100px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          borderRadius: 12,
          border: `1px solid ${colors.border}`,
          padding: 16,
          display: 'grid',
          gap: 12,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: 18 }}>
              {step === 'preview' ? 'Revisar importación' : 'Carga masiva de contactos'}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: 13 }}>
              {step === 'preview'
                ? 'Asocia cada contacto por el nombre del proveedor. Duplicados (mismo correo o nombre en el proveedor) se omiten.'
                : 'Sube la plantilla Excel (.xls) o un CSV (;). Luego revisas la tabla antes de importar.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.muted,
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Upload size={15} />
                Subir CSV / plantilla .xls
                <input
                  type="file"
                  accept=".csv,.xls,text/csv,text/plain,application/vnd.ms-excel"
                  hidden
                  onChange={handleFile}
                />
              </label>
              <button
                type="button"
                onClick={downloadSupplierContactsExcelTemplate}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Descargar plantilla Excel
              </button>
            </div>
            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                setLocalError('');
              }}
              placeholder={`full_name;role;email;phone;supplier_name;status;created_by\n${TEMPLATE_EXAMPLE_CELLS.join(';')}`}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                resize: 'vertical',
              }}
            />
            <div style={{ fontSize: 12, color: colors.muted }}>
              {parsed.error
                ? parsed.error
                : `${parsed.rows.length} fila(s) detectadas. Continúa para revisarlas.`}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                fontSize: 12,
                color: colors.muted,
              }}
            >
              <span>
                {previewRows.length} fila(s)
                {invalidFieldCount > 0
                  ? ` · ${invalidFieldCount} campo(s) con error`
                  : ' · formato OK'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setStep('source');
                  setLocalError('');
                }}
                disabled={isImporting}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.text,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                }}
              >
                Volver al archivo
              </button>
            </div>
            {errorSummaries.length ? (
              <div
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid #fecaca',
                  background: isDarkMode ? '#450a0a' : '#fef2f2',
                  color: isDarkMode ? '#fecaca' : '#991b1b',
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  Corrige estos campos para poder importar:
                </div>
                {errorSummaries.slice(0, 8).map((item) => (
                  <div key={`sum-${item.row}-${item.field}`}>
                    Fila {item.row} · {item.label}: {item.message}
                  </div>
                ))}
                {errorSummaries.length > 8 ? (
                  <div>… y {errorSummaries.length - 8} más</div>
                ) : null}
              </div>
            ) : null}
            <div
              style={{
                overflow: 'auto',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                maxHeight: '58vh',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }}>#</th>
                    {TEMPLATE_HEADERS.map((key) => (
                      <th key={key} style={{ padding: '8px 10px', position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>
                        {COLUMN_LABELS[key]}
                      </th>
                    ))}
                    <th style={{ padding: '8px 10px', position: 'sticky', top: 0 }} />
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => {
                    const errors = rowErrors[index] || {};
                    return (
                      <tr key={`row-${index}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '6px 10px', color: colors.muted }}>{index + 1}</td>
                        {TEMPLATE_HEADERS.map((key) => {
                          const fieldError = errors[key];
                          return (
                            <td key={key} style={{ padding: '4px 6px', verticalAlign: 'top' }}>
                              <input
                                value={row[key] || ''}
                                onChange={(e) => updatePreviewCell(index, key, e.target.value)}
                                aria-invalid={Boolean(fieldError)}
                                style={{
                                  width:
                                    key === 'full_name' || key === 'supplier_name' || key === 'email'
                                      ? 140
                                      : 100,
                                  height: 30,
                                  borderRadius: 6,
                                  border: `1px solid ${fieldError ? '#dc2626' : colors.inputBorder}`,
                                  background: fieldError
                                    ? isDarkMode
                                      ? '#450a0a'
                                      : '#fef2f2'
                                    : colors.inputBg,
                                  color: fieldError ? (isDarkMode ? '#fecaca' : '#991b1b') : colors.text,
                                  padding: '0 8px',
                                  fontSize: 12,
                                  boxSizing: 'border-box',
                                  fontWeight: fieldError ? 600 : 400,
                                }}
                                title={fieldError || ''}
                              />
                              {fieldError ? (
                                <div
                                  style={{
                                    color: isDarkMode ? '#fecaca' : '#b91c1c',
                                    fontSize: 10,
                                    marginTop: 3,
                                    lineHeight: 1.25,
                                    maxWidth: 140,
                                    fontWeight: 600,
                                  }}
                                >
                                  {fieldError}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                        <td style={{ padding: '4px 8px' }}>
                          <button
                            type="button"
                            onClick={() => removePreviewRow(index)}
                            disabled={isImporting}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.muted,
                              cursor: 'pointer',
                              padding: 4,
                              display: 'inline-flex',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {(localError || localSuccess || result) && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              color: colors.text,
              background: isDarkMode ? '#09090b' : '#f8fafc',
            }}
          >
            {localSuccess ? (
              <div style={{ color: isDarkMode ? '#86efac' : '#15803d', fontWeight: 600 }}>
                {localSuccess}
              </div>
            ) : null}
            {localError ? <div style={{ color: '#dc2626' }}>{localError}</div> : null}
            {!localSuccess && result ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <div>
                  Creados: <strong>{result.created || 0}</strong> · Omitidos:{' '}
                  <strong>{result.skipped || 0}</strong> · Errores:{' '}
                  <strong>{result.errors || 0}</strong>
                </div>
                {Array.isArray(result.error_rows) && result.error_rows.length > 0 ? (
                  <div style={{ color: '#dc2626', fontSize: 12 }}>
                    {result.error_rows.slice(0, 5).map((row) => (
                      <div key={`err-${row.row}`}>
                        Fila {row.row}: {row.detail}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.inputBorder}`,
              background: 'transparent',
              color: colors.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {step === 'source' ? (
            <button
              type="button"
              onClick={handleContinueFromSource}
              disabled={Boolean(parsed.error) || !parsed.rows.length}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: !parsed.rows.length || parsed.error ? '#94a3b8' : '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: !parsed.rows.length || parsed.error ? 'not-allowed' : 'pointer',
              }}
            >
              Revisar datos
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isImporting || Boolean(localSuccess) || invalidFieldCount > 0 || !previewRows.length}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? '#86efac'
                    : '#16a34a',
                color: '#fff',
                fontWeight: 600,
                cursor:
                  isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isImporting ? 'Importando…' : localSuccess ? 'Listo' : `Importar ${previewRows.length || ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierContactsBulkImportModal;
